export const COOKIE_CONSENT_STORAGE_KEY = "beton2go_cookie_consent";
export const LEGACY_COOKIE_CONSENT_STORAGE_KEY = "cookies_accepted";
export const COOKIE_CONSENT_VERSION = "2026-04-16";

export type CookieConsentCategory = "externalMedia";

export type CookieConsentPreferences = {
  necessary: true;
  externalMedia: boolean;
};

export type CookieConsentDecision = "accepted" | "rejected" | "customized";

export type StoredCookieConsent = {
  version: string;
  decidedAt: string;
  decision: CookieConsentDecision;
  preferences: CookieConsentPreferences;
};

export const defaultCookieConsentPreferences = (): CookieConsentPreferences => ({
  necessary: true,
  externalMedia: false,
});

export const getDefaultCookieConsent = (): StoredCookieConsent => ({
  version: COOKIE_CONSENT_VERSION,
  decidedAt: new Date().toISOString(),
  decision: "rejected",
  preferences: defaultCookieConsentPreferences(),
});

const isValidStoredConsent = (value: unknown): value is StoredCookieConsent => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const maybeConsent = value as Partial<StoredCookieConsent>;

  return (
    typeof maybeConsent.version === "string" &&
    typeof maybeConsent.decidedAt === "string" &&
    (maybeConsent.decision === "accepted" ||
      maybeConsent.decision === "rejected" ||
      maybeConsent.decision === "customized") &&
    typeof maybeConsent.preferences === "object" &&
    maybeConsent.preferences !== null &&
    (maybeConsent.preferences as Partial<CookieConsentPreferences>).necessary === true &&
    typeof (maybeConsent.preferences as Partial<CookieConsentPreferences>).externalMedia === "boolean"
  );
};

export const buildStoredCookieConsent = (
  preferences: Partial<Omit<CookieConsentPreferences, "necessary">>,
): StoredCookieConsent => {
  const normalizedPreferences: CookieConsentPreferences = {
    necessary: true,
    externalMedia: preferences.externalMedia ?? false,
  };

  const decision: CookieConsentDecision = normalizedPreferences.externalMedia
    ? "accepted"
    : "rejected";

  return {
    version: COOKIE_CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
    decision,
    preferences: normalizedPreferences,
  };
};

export const parseStoredCookieConsent = (rawValue: string | null): StoredCookieConsent | null => {
  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as unknown;

    if (!isValidStoredConsent(parsedValue)) {
      return null;
    }

    if (parsedValue.version !== COOKIE_CONSENT_VERSION) {
      return null;
    }

    return parsedValue;
  } catch {
    return null;
  }
};

export const migrateLegacyCookieConsent = (legacyValue: string | null): StoredCookieConsent | null => {
  if (legacyValue === "true") {
    return buildStoredCookieConsent({ externalMedia: true });
  }

  if (legacyValue === "false") {
    return buildStoredCookieConsent({ externalMedia: false });
  }

  return null;
};
